import React, { useState, useEffect, useRef } from 'react';
import ScrollSpy from 'react-ui-scrollspy';
import { Link } from 'gatsby';
// Components
import Icons from '../icon/Icons';
import RichTextRenderer from '../richTextRenderer';
// Interfaces
import { Link as LinkInterface } from '../../interfaces/link';
import { Icon as IconInterface } from '../../interfaces/icon';
interface SectionWithSideNavigationProps {
  sideNavigation: Array<{
    title: string;
    categories: Array<LinkInterface>;
  }>;
  sideNavigationExtraClasses: string;
  contentToNavigate: Array<{
    collection: { icons: Array<IconInterface> };
  }>;
  contentExtraClasses: string;
}

function SectionWithSideNavigation({
  sideNavigation,
  sideNavigationExtraClasses,
  contentToNavigate,
  contentExtraClasses,
}: SectionWithSideNavigationProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [currentItem, setCurrentItem] = useState(0);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const htmlElement = document.querySelector('html');
    htmlElement.classList.add('cl-smooth-scroll');
    return () => {
      htmlElement.classList.remove('cl-smooth-scroll');
    };
  });

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.addEventListener('keydown', handleKeyDown);

      return () => {
        window.removeEventListener('keydown', handleKeyDown);
      };
    }
  });

  useEffect(() => {
    function handleClickOutside(event: any) {
      /* @ts-ignore */
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [wrapperRef]);

  const handleKeyDown = (e: any) => {
    if (isOpen && sideNavigation[0]) {
      e.preventDefault();

      switch (e.keyCode) {
        // KeyDown
        case 40:
          if (currentItem === sideNavigation[0].categories.length - 1) {
            setCurrentItem(0);
          } else {
            setCurrentItem(currentItem + 1);
          }
          break;
        // KeyUp
        case 38:
          if (currentItem === 0) {
            setCurrentItem(sideNavigation[0].categories.length - 1);
          } else {
            setCurrentItem(currentItem - 1);
          }
          break;
        // Escape
        case 27:
          setCurrentItem(1);
          setIsOpen(false);
          break;
        default:
          break;
      }
    }
  };

  const contentAnchors: Array<string> = [];

  return (
    <section className='pb-40 cl-section cl-section--bg-bottom-04c lg:cl-section--bg-bottom-01d'>
      <div className='cl-section__bg'></div>
      <div className='cl-container'>
        <div className='cl-row'>
          <div className='self-stretch col-span-4 lg:col-span-3 cl-col'>
            <div className='cl-side-nav__dropdown' id='dropdown' ref={wrapperRef}>
              <button
                className='cl-button cl-button--lg cl-button--primary cl-button--normal cl-button--elevated'
                onClick={() => setIsOpen(!isOpen)}
                aria-expanded={isOpen ? 'true' : 'false'}
              >
                {sideNavigation[0] && <span>{sideNavigation[0].title}</span>}

                <span className='relative only:-mx-5'>
                  <svg
                    className='w-4 h-4'
                    width='16'
                    height='16'
                    viewBox='0 0 16 16'
                    fill='none'
                    xmlns='http://www.w3.org/2000/svg'
                    stroke='currentColor'
                    strokeWidth='1.5'
                    aria-labelledby='t-01 d-01'
                    role='graphics-symbol'
                  >
                    <title id='t-01'>Dropdown Icon</title>
                    <desc id='d-01'>Dropdown Icon</desc>
                    <path
                      d='M12 6L8.18844 9.8112C8.16371 9.83597 8.13434 9.85562 8.102 9.86903C8.06967 9.88243 8.035 9.88933 8 9.88933C7.965 9.88933 7.93033 9.88243 7.898 9.86903C7.86566 9.85562 7.83629 9.83597 7.81156 9.8112L4 6'
                      stroke='currentColor'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    />
                  </svg>
                </span>
              </button>
              <ul className={`${isOpen ? 'flex' : 'hidden'} cl-side-nav__dropdown-list`}>
                {sideNavigation[0] &&
                  sideNavigation[0].categories.map((item, index) => {
                    return (
                      <li key={index}>
                        <a
                          className='cl-side-nav__dropdown-link'
                          href={`#${item.linkName.replace(/\s+/g, '')}`}
                          onClick={() => setIsOpen(false)}
                        >
                          <span className='flex flex-col gap-1 overflow-hidden whitespace-nowrap'>
                            <span className='leading-5 truncate'>{item.linkName}</span>
                          </span>
                        </a>
                      </li>
                    );
                  })}
              </ul>
            </div>
            <nav
              className={`cl-side-nav hidden lg:block cl-col--sticky ${
                sideNavigationExtraClasses ? sideNavigationExtraClasses : ''
              }`}
            >
              {sideNavigation.map((navigation, sideNavigationIndex: number) => {
                // If content doesn't match the categories length the user is on a subcategory
                const isOnASubCategory =
                  contentToNavigate.length === sideNavigation[0]?.categories.length;

                return (
                  <React.Fragment key={sideNavigationIndex}>
                    {/* Getting the first word only and display as a title */}
                    {navigation.title && sideNavigationIndex === 0 ? (
                      <h3 className='cl-side-nav__title'>{navigation.title.split(' ')[0]}</h3>
                    ) : (
                      <h3 className='cl-side-nav__title'>{navigation.title}</h3>
                    )}

                    <ul key={sideNavigationIndex} className='cl-side-nav__menu'>
                      {navigation.categories.map((category) => {
                        // Generating Anchor Tags
                        let anchorId = '';
                        if (category.externalUrl) {
                          if (category.externalUrl.indexOf('#') !== -1 && isOnASubCategory) {
                            anchorId = category.linkName.replace(/[0-9]+\.\s/i, '');

                            anchorId = anchorId.replace(/ +/g, '');

                            contentAnchors.push(anchorId);
                          }
                        }

                        return (
                          <li key={category.id} className='cl-side-nav__menu-item'>
                            <Link
                              to={anchorId ? '#' + anchorId : category.externalUrl}
                              activeClassName='active'
                              className='cl-side-nav__menu-item-link'
                              data-to-scrollspy-id={`${sideNavigationIndex == 0 ? anchorId : ' '}`}
                            >
                              {category.linkName}
                            </Link>
                          </li>
                        );
                      })}
                    </ul>
                  </React.Fragment>
                );
              })}
            </nav>
          </div>

          {/* Content */}
          {contentToNavigate && (
            <div
              className={`col-span-4 lg:col-span-9 cl-col self-start cl-side-nav-content ${
                contentExtraClasses ? contentExtraClasses : ''
              }`}
            >
              {typeof window !== 'undefined' && (
                <ScrollSpy
                  activeClass='cl-side-nav__menu-item-link--active'
                  offsetBottom={100}
                  scrollThrottle={80}
                  useBoxMethod
                >
                  {contentToNavigate.map((content: any, index: number) => {
                    return (
                      <div
                        id={`${contentAnchors[index]?.replace('#', '')}`}
                        className='mb-20 cl-text-block last:mb-0'
                        key={content.collection.id}
                      >
                        {content.title && <h2>{content.title}</h2>}

                        {/* Side Navigation Configured with Icons && Text as Content */}
                        {content.collection.sys.contentType.sys.id === 'richtext' ? (
                          <RichTextRenderer
                            text={content.collection.body}
                            extraClasses={content.collection.extraClasses}
                          />
                        ) : (
                          <Icons
                            icons={content.collection.icons}
                            columnSpan={content.collection.columnSpan}
                            extraClasses={content.collection.extraClasses}
                          />
                        )}
                      </div>
                    );
                  })}
                </ScrollSpy>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

export default SectionWithSideNavigation;
